import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { setSentryUser, clearSentryUser } from '../lib/sentry'

const AuthContext = createContext({})

// Persist the sign-in provider so Login.js can hint "you used Google last time"
// on return visits. Saved on every observed session (initial getSession +
// onAuthStateChange) because Supabase's OAuth flow lands the session via
// redirect — there's no client-side "sign in succeeded" callback to hook into
// otherwise. Survives signOut intentionally; the whole point is the next-visit
// hint after the user has been logged out. Cleared if the slot is missing
// app_metadata.provider (defensive — should never happen for real sessions).
const SIGNIN_PROVIDER_KEY = 'an.lastSignInProvider'
const rememberSignInProvider = (authUser) => {
  try {
    const provider = authUser?.app_metadata?.provider
    if (provider) localStorage.setItem(SIGNIN_PROVIDER_KEY, provider)
  } catch { /* private mode / storage disabled — ignore */ }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  // Set to true when Supabase fires the PASSWORD_RECOVERY auth-state
  // event (i.e., the user just clicked a recovery link). The router
  // hard-redirects to /reset-password until the user finishes resetting
  // and ResetPassword.js calls clearRecovery(). Without this flag the
  // recovery session looks identical to a normal signed-in session, so
  // the user would land at / with no way to set their new password.
  const [recoveryActive, setRecoveryActive] = useState(false)

  // Mirror the current user id into a ref so the mount-only visibility
  // handler below can compare against the LATEST id, not the null
  // captured in the closure on first mount.
  const userIdRef = useRef(null)
  useEffect(() => { userIdRef.current = user?.id || null }, [user?.id])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        rememberSignInProvider(session.user)
        await fetchProfile(session.user)
      }
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') setRecoveryActive(true)
        setUser(session?.user ?? null)
        if (session?.user) {
          rememberSignInProvider(session.user)
          fetchProfile(session.user)
        }
        else { setProfile(null); clearSentryUser() }
      }
    )

    // PWA (iOS + Android): re-check session when app comes back into focus.
    // Only update state if the session was lost or the user actually changed —
    // do NOT re-fetch profile on a simple token refresh (same user ID) because
    // the profile query can fail mid-refresh and leave downstream UI in an
    // inconsistent profile=null state.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session?.user) {
          setUser(null)
          setProfile(null)
          clearSentryUser()
          return
        }
        if (userIdRef.current === session.user.id) return
        setUser(session.user)
        fetchProfile(session.user)
      })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const fetchProfile = async (authUser) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', authUser.id)
      .single()

    if (data) {
      // Profile exists — but if full_name is missing and Google provided one, fill it in
      if (!data.full_name) {
        const googleName =
          authUser.user_metadata?.full_name ||
          authUser.user_metadata?.name ||
          null
        if (googleName) {
          await supabase.from('profiles').update({ full_name: googleName }).eq('id', authUser.id)
          setProfile({ ...data, full_name: googleName })
          return
        }
      }
      setProfile(data)
      setSentryUser(authUser, data)
    } else {
      // No profile row visible. Could be (a) genuinely no row yet, or
      // (b) a transient RLS/auth blip where the SELECT returned null even
      // though a row exists. In case (b), we must NOT overwrite the
      // existing full_name with Google metadata. Use ignoreDuplicates so
      // we only insert when no row exists; do nothing to an existing row.
      const googleName =
        authUser.user_metadata?.full_name ||
        authUser.user_metadata?.name ||
        null
      // Include email so new profiles are immediately email-routable
      // by the notification webhook (mig 71 backfills existing rows;
      // this handles future signups). authUser.email is set by Supabase
      // Auth at signup for both email/password and Google paths.
      await supabase.from('profiles').upsert({
        id: authUser.id,
        full_name: googleName || null,
        email: authUser.email || null,
      }, { onConflict: 'id', ignoreDuplicates: true })

      // Re-fetch: if a row already existed (case b), the upsert was a no-op
      // and we still need the real row. If the row was newly created (case a),
      // this picks it up too.
      const { data: real } = await supabase
        .from('profiles').select('*').eq('id', authUser.id).single()
      setProfile(real)
      setSentryUser(authUser, real)
    }
  }

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user)
  }, [user])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  // Called by ResetPassword.js after a successful supabase.auth.updateUser
  // call, so the router stops forcing the /reset-password redirect and
  // the user lands on / as a normally signed-in user.
  const clearRecovery = useCallback(() => setRecoveryActive(false), [])

  // Memoize the provider value so consumers don't re-render on every
  // parent render — only when an actual auth/profile slice changes.
  const value = useMemo(
    () => ({ user, profile, loading, signOut, refreshProfile, recoveryActive, clearRecovery }),
    [user, profile, loading, signOut, refreshProfile, recoveryActive, clearRecovery]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
